import React, { useState, useEffect } from "react";
import {
  MdSend,
  MdEmail,
  MdWhatsapp,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
} from "react-icons/md";
import { fetchWithAuth } from "services/api";

// Define the Category interface
interface Category {
  id: number;
  name: string;
  description: string;
  is_active: number;
  created_at: string | null;
  updated_at: string | null;
}

// Define the Questionnaire interface
interface Questionnaire {
  id: number;
  name: string;
  code: string;
  description: string;
  category: Category | null;
}

interface AssignQuestionnaireModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientName: string;
  patientId: number;
  onAssign: (
    questionnaires: number[],
    deliveryMethods: { email: boolean; whatsapp: boolean }
  ) => void;
}

const AssignQuestionnaireModal: React.FC<AssignQuestionnaireModalProps> = ({
  isOpen,
  onClose,
  patientName,
  patientId,
  onAssign,
}) => {
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [loadingQuestionnaires, setLoadingQuestionnaires] = useState(false);
  const [selectedQuestionnaires, setSelectedQuestionnaires] = useState<
    number[]
  >([]);
  const [deliveryMethods, setDeliveryMethods] = useState({
    email: true,
    whatsapp: true,
  });
  const [expirationDate, setExpirationDate] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(
    new Set()
  );

  useEffect(() => {
    if (isOpen) {
      fetchQuestionnaires();
      // Set default expiration date to 30 days from now
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 30);
      setExpirationDate(defaultDate.toISOString().split("T")[0]);
    }
  }, [isOpen]);

  // Function to fetch questionnaires
  const fetchQuestionnaires = async () => {
    try {
      setLoadingQuestionnaires(true);
      const response = await fetchWithAuth("/questionnaires");
      if (!response.success) {
        throw new Error("Failed to fetch questionnaires");
      }
      const data = response.data;
      // Format the data to use name - code for label and id for value
      const formattedQuestionnaires = (data || []).map(
        (questionnaire: {
          id: number;
          name: string;
          code: string;
          description: string;
          category: Category | null;
        }) => ({
          id: questionnaire.id,
          name: `${questionnaire.name} - ${questionnaire.code}`,
          code: questionnaire.code,
          description: questionnaire.description,
          category: questionnaire.category,
        })
      );
      setQuestionnaires(formattedQuestionnaires);

      // Auto-expand categories by default
      const categories = new Set<number>();
      for (const questionnaire of formattedQuestionnaires) {
        if (questionnaire.category) {
          categories.add(questionnaire.category.id);
        }
      }
      setExpandedCategories(categories);
    } catch (err) {
      console.error("Error fetching questionnaires:", err);
    } finally {
      setLoadingQuestionnaires(false);
    }
  };

  // Function to toggle questionnaire selection
  const toggleQuestionnaireSelection = (id: number) => {
    setSelectedQuestionnaires((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Function to toggle all questionnaires in a category
  const toggleCategorySelection = (
    categoryId: number,
    questionnairesInCategory: Questionnaire[]
  ) => {
    const idsInCategory = questionnairesInCategory.map((q) => q.id);

    // Check if all questionnaires in this category are already selected
    const allSelected = idsInCategory.every((id) =>
      selectedQuestionnaires.includes(id)
    );

    if (allSelected) {
      // Unselect all questionnaires in this category
      setSelectedQuestionnaires((prev) =>
        prev.filter((id) => !idsInCategory.includes(id))
      );
    } else {
      // Select all questionnaires in this category
      setSelectedQuestionnaires((prev) => {
        const newSelection = [...prev];
        idsInCategory.forEach((id) => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  // Function to toggle category expansion
  const toggleCategoryExpansion = (categoryId: number) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Function to check if all questionnaires in a category are selected
  const isCategoryFullySelected = (
    questionnairesInCategory: Questionnaire[]
  ) => {
    return questionnairesInCategory.every((q) =>
      selectedQuestionnaires.includes(q.id)
    );
  };

  // Function to check if some questionnaires in a category are selected
  const isCategoryPartiallySelected = (
    questionnairesInCategory: Questionnaire[]
  ) => {
    return (
      questionnairesInCategory.some((q) =>
        selectedQuestionnaires.includes(q.id)
      ) &&
      !questionnairesInCategory.every((q) =>
        selectedQuestionnaires.includes(q.id)
      )
    );
  };

  // Function to toggle delivery method
  const toggleDeliveryMethod = (method: "email" | "whatsapp") => {
    setDeliveryMethods((prev) => ({
      ...prev,
      [method]: !prev[method],
    }));
  };

  // Group questionnaires by category
  const groupedQuestionnaires = questionnaires.reduce((acc, questionnaire) => {
    const categoryId = questionnaire.category ? questionnaire.category.id : 0; // Use 0 for uncategorized
    if (!acc[categoryId]) {
      acc[categoryId] = {
        category: questionnaire.category || {
          id: 0,
          name: "Sin Categoría",
          description: "Cuestionarios sin categoría asignada",
          is_active: 1,
          created_at: null,
          updated_at: null,
        },
        questionnaires: [],
      };
    }
    acc[categoryId].questionnaires.push(questionnaire);
    return acc;
  }, {} as Record<number, { category: Category; questionnaires: Questionnaire[] }>);

  // Function to handle assignment submission
  const handleAssignQuestionnaires = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Convert delivery methods to array format
      const deliveryArray: string[] = [];
      if (deliveryMethods.email) deliveryArray.push("email");
      if (deliveryMethods.whatsapp) deliveryArray.push("whatsapp");

      // Format expiration date with time
      const expiresAt = `${expirationDate}T23:59:59Z`;

      // Create the payload
      const payload = {
        patient_id: patientId,
        questionnaire_ids: selectedQuestionnaires,
        expires_at: expiresAt,
        delivery: deliveryArray,
      };

      // Make the POST request
      const response = await fetchWithAuth("/questionnaire-assignments", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.success) {
        const errorData = response;
        throw new Error(errorData.message || "Failed to assign questionnaires");
      }

      // Call the onAssign callback and reset the selected questionnaires
      onAssign(selectedQuestionnaires, deliveryMethods);
      setSelectedQuestionnaires([]);
      onClose();
    } catch (err) {
      console.error("Error assigning questionnaires:", err);
      setSubmitError(
        err instanceof Error ? err.message : "Unknown error occurred"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl dark:bg-navy-700">
        <h2 className="mb-6 text-xl font-bold text-navy-700 dark:text-white">
          Asignar cuestionarios a {patientName}
        </h2>

        {/* Questionnaires Section */}
        <div className="mb-6">
          <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
            Seleccionar cuestionarios
          </h3>

          {loadingQuestionnaires ? (
            <div className="flex justify-center py-4">
              <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-brand-500"></div>
            </div>
          ) : Object.keys(groupedQuestionnaires).length === 0 ? (
            <p className="py-4 text-center text-gray-500">
              No hay cuestionarios disponibles
            </p>
          ) : (
            <div className="max-h-80 overflow-y-auto rounded-lg border border-gray-200 p-4 dark:border-navy-600">
              <div className="divide-y divide-gray-200 dark:divide-navy-600">
                {Object.values(groupedQuestionnaires).map(
                  ({ category, questionnaires }) => {
                    const isExpanded = expandedCategories.has(category.id);
                    const isFullySelected =
                      isCategoryFullySelected(questionnaires);
                    const isPartiallySelected =
                      isCategoryPartiallySelected(questionnaires);

                    return (
                      <div key={category.id} className="py-2">
                        <div className="flex items-center">
                          <div className="flex flex-1 items-center">
                            <div className="mr-2">
                              <input
                                type="checkbox"
                                id={`category-${category.id}`}
                                checked={isFullySelected}
                                ref={(el) => {
                                  if (el) {
                                    // Apply indeterminate state for partial selection
                                    el.indeterminate = isPartiallySelected;
                                  }
                                }}
                                onChange={() =>
                                  toggleCategorySelection(
                                    category.id,
                                    questionnaires
                                  )
                                }
                                className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700"
                              />
                            </div>
                            <div
                              className="flex flex-1 cursor-pointer items-center py-1"
                              onClick={() =>
                                toggleCategoryExpansion(category.id)
                              }
                            >
                              <label
                                htmlFor={`category-${category.id}`}
                                className="flex flex-1 cursor-pointer items-center font-medium text-navy-700 dark:text-white"
                              >
                                {category.name}
                                <span className="ml-2 text-xs text-gray-500">
                                  ({questionnaires.length})
                                </span>
                              </label>
                              {isExpanded ? (
                                <MdKeyboardArrowUp className="h-5 w-5 text-gray-500" />
                              ) : (
                                <MdKeyboardArrowDown className="h-5 w-5 text-gray-500" />
                              )}
                            </div>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="ml-6 mt-2 space-y-2">
                            {questionnaires.map((questionnaire) => (
                              <div
                                key={questionnaire.id}
                                className="flex items-start"
                              >
                                <input
                                  type="checkbox"
                                  id={`questionnaire-${questionnaire.id}`}
                                  checked={selectedQuestionnaires.includes(
                                    questionnaire.id
                                  )}
                                  onChange={() =>
                                    toggleQuestionnaireSelection(
                                      questionnaire.id
                                    )
                                  }
                                  className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700"
                                />
                                <label
                                  htmlFor={`questionnaire-${questionnaire.id}`}
                                  className="ml-2 flex flex-col text-sm"
                                >
                                  <span className="font-medium text-navy-700 dark:text-white">
                                    {questionnaire.name}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {questionnaire.description}
                                  </span>
                                </label>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          )}
        </div>

        {/* Expiration Date Section */}
        <div className="mb-6">
          <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
            Fecha de expiración
          </h3>
          <div className="flex items-center">
            <input
              type="date"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              min={new Date().toISOString().split("T")[0]}
            />
            <span className="ml-3 text-sm text-gray-500">
              Los cuestionarios expirarán al final de este día
            </span>
          </div>
        </div>

        {/* Delivery Methods Section */}
        <div className="mb-6">
          <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
            Método de envío
          </h3>
          <div className="flex items-center gap-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="email-delivery"
                checked={deliveryMethods.email}
                onChange={() => toggleDeliveryMethod("email")}
                className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700"
              />
              <label
                htmlFor="email-delivery"
                className="ml-2 flex items-center gap-1 text-sm font-medium text-navy-700 dark:text-white"
              >
                <MdEmail className="h-5 w-5 text-blue-500" />
                Email
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="whatsapp-delivery"
                checked={deliveryMethods.whatsapp}
                onChange={() => toggleDeliveryMethod("whatsapp")}
                className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700"
              />
              <label
                htmlFor="whatsapp-delivery"
                className="ml-2 flex items-center gap-1 text-sm font-medium text-navy-700 dark:text-white"
              >
                <MdWhatsapp className="h-5 w-5 text-green-500" />
                WhatsApp
              </label>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {submitError && (
          <div className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700">
            {submitError}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-600"
          >
            Cancelar
          </button>
          <button
            onClick={handleAssignQuestionnaires}
            className="flex items-center gap-1 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={
              isSubmitting ||
              selectedQuestionnaires.length === 0 ||
              (!deliveryMethods.email && !deliveryMethods.whatsapp) ||
              !expirationDate
            }
          >
            {isSubmitting ? (
              <>
                <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-white"></div>
                <span>Enviando...</span>
              </>
            ) : (
              <>
                <MdSend className="h-5 w-5" />
                <span>Enviar</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignQuestionnaireModal;
